import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { Dispatch } from "../../common/Dispatch";
import { MarinerProfile } from "../../common/MarinerProfile";
import { User, UserGroup } from "../../common/User";
import { staggeredBaseQuery } from "./staggeredBaseQuery";

type CognitoUserAttributes = {
  name: string;
  dateOfBirth: string;
};

type CognitoUserRegistration = {
  email: string;
  password: string;
  name: string;
  dateOfBirth: string;
};

export const userApi = createApi({
  reducerPath: "userApi",
  tagTypes: ["User"],
  baseQuery: staggeredBaseQuery(""),
  endpoints: (builder) => ({
    getUsersByGroup: builder.query<User[], UserGroup>({
      query: (userGroup) => {
        return {
          method: "GET",
          url: `users/groups/${userGroup}s`, // when searching by group names, they are plural
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "User" as const, id })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
    }),
    getUserProfileDispatch: builder.query<
      {
        captain: boolean;
        trainingAssistant: boolean;
        profile: MarinerProfile;
        dispatch: Dispatch;
        group: string;
      },
      {}
    >({
      query: () => {
        return {
          method: "GET",
          url: `users/userProfileDispatch`,
        };
      },
    }),
    putCurrentUserAttributes: builder.mutation<
      CognitoUserAttributes,
      { attributes: Partial<CognitoUserAttributes> }
    >({
      query: ({ attributes }) => {
        return {
          method: "PUT",
          url: `users/user`,
          body: attributes,
        };
      },
    }),
    register: builder.mutation<
      CognitoUserRegistration,
      { attributes: Partial<CognitoUserRegistration> }
    >({
      query: ({ attributes }) => {
        return {
          method: "POST",
          url: `users/register`,
          body: attributes,
        };
      },
    }),
    getVesselsForVesselExec: builder.query<number[], number>({
      query: (userId) => {
        return {
          method: "GET",
          url: `vesselExecPermissions/vessels/${userId}`,
        };
      },
    })
  }),
});

export const {
  useGetUserProfileDispatchQuery,
  useGetUsersByGroupQuery,
  usePutCurrentUserAttributesMutation,
  useRegisterMutation,
  useGetVesselsForVesselExecQuery,
} = userApi;
